import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import ExportExcel from "../../../../utils/excelExport";

const fmt = (val, decimals = 2) =>
  val != null && val !== "" && !isNaN(Number(val))
    ? new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(Number(val))
    : "-";

const columns = [
  { id: "customerGradeMark", label: "Code",             align: "center", numeric: false },
  { id: "itemId",            label: "Item ID",          align: "center", numeric: true  },
  { id: "name",              label: "Name",             align: "left",   numeric: false },
  { id: "itemNicotine",      label: "NIC",              align: "center", numeric: true  },
  { id: "itemSugar",         label: "SUGAR",            align: "center", numeric: true  },
  { id: "unitCost",          label: "Unit Cost",        align: "right",  numeric: true  },
  { id: "currency",          label: "Currency",         align: "center", numeric: false },
  { id: "unitCostInUsd",     label: "Unit Cost In USD", align: "right",  numeric: true  },
  { id: "totalIncoming",     label: "Incoming Qty",     align: "right",  numeric: true  },
  { id: "totalOutgoing",     label: "Outgoing Qty",     align: "right",  numeric: true  },
  { id: "netQuantity",       label: "Sum of Quantity",  align: "right",  numeric: true  },
  { id: "totalAmountUsd",    label: "Sum of Amount In USD", align: "right", numeric: true },
];

function enrichRow(raw) {
  const incoming = Number(raw.totalIncoming) || 0;
  const outgoing = Number(raw.totalOutgoing) || 0;
  const netQuantity = incoming - outgoing;
  const unitCostInUsd = Number(raw.unitCostInUsd) || 0;
  const totalAmountUsd = netQuantity * unitCostInUsd;
  const name = [raw.itemOrigin, raw.itemType, raw.itemBuyingGrade, raw.itemCrop]
    .filter(Boolean)
    .join(" ");
  return { ...raw, name, netQuantity, totalAmountUsd };
}

function descendingComparator(a, b, orderBy) {
  const av = a[orderBy];
  const bv = b[orderBy];
  if (bv == null && av == null) return 0;
  if (bv == null) return -1;
  if (av == null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

const CustomerGradeMarkInventoryButton = ({ rows = [] }) => {
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("customerGradeMark");
  const [splitByGrade, setSplitByGrade] = useState(false);

  // Aggregate using already-loaded warehouse inventory rows — zero extra API calls
  const aggregated = useMemo(() => {
    // Step 1: build warehouseId+itemId → customerGradeMark from Incoming records.
    // This lets us correctly attribute batch-consumption Outgoing records (which have
    // no SI and therefore no customerGradeMark) to the right grade mark.
    const wkToGradeMark = {};
    rows.forEach((row) => {
      if (row?.type === "Incoming" && row?.customerGradeMark && row?.warehouseId != null && row?.itemId != null) {
        const wk = `${row.warehouseId}-${row.itemId}`;
        if (!wkToGradeMark[wk]) wkToGradeMark[wk] = row.customerGradeMark;
      }
    });

    // Step 2: aggregate by customerGradeMark
    const cgmMap = {};
    rows.forEach((row) => {
      let key = row?.customerGradeMark;

      // Attribute null-CGM Outgoing (batch consumption) to the incoming grade mark
      if (!key && row?.type === "Outgoing" && row?.warehouseId != null && row?.itemId != null) {
        key = wkToGradeMark[`${row.warehouseId}-${row.itemId}`];
      }

      if (!key) return;

      if (!cgmMap[key]) {
        // Seed metadata from the first Incoming row for this key
        const seed = rows.find((r) => r?.customerGradeMark === key && r?.type === "Incoming") ||
          rows.find((r) => r?.customerGradeMark === key) || row;
        cgmMap[key] = {
          customerGradeMark: key,
          itemId: seed?.itemId ?? null,
          itemNicotine: seed?.itemNicotine ?? null,
          itemSugar: seed?.itemSugar ?? null,
          itemOrigin: seed?.itemOrigin ?? "",
          itemType: seed?.itemType ?? "",
          itemBuyingGrade: seed?.itemBuyingGrade ?? "",
          itemCrop: seed?.itemCrop ?? "",
          unitCost: seed?.purchaseOrderFinalPrice ?? null,
          currency: seed?.purchaseOrderFinalPriceCurrency ?? null,
          unitCostInUsd: seed?.purchaseOrderUsdFinalPrice ?? null,
          totalIncoming: 0,
          totalOutgoing: 0,
        };
      }

      if (row?.type === "Incoming") {
        cgmMap[key].totalIncoming += row?.entryDate ? (Number(row?.shipmentQuantity) || 0) : 0;
      } else if (row?.type === "Outgoing") {
        cgmMap[key].totalOutgoing += Number(row?.shipmentQuantity ?? row?.shippedAmount) || 0;
      }

      if (cgmMap[key].unitCost == null && row?.purchaseOrderFinalPrice != null && row?.type === "Incoming") {
        cgmMap[key].unitCost = row.purchaseOrderFinalPrice;
        cgmMap[key].currency = row.purchaseOrderFinalPriceCurrency ?? null;
        cgmMap[key].unitCostInUsd = row.purchaseOrderUsdFinalPrice ?? null;
      }
    });

    // Step 3: enrich each cgm entry individually so totalAmountUsd is computed at its own price
    const enrichedCgm = Object.values(cgmMap).map(enrichRow);

    // Split mode: return one row per CGM code without merging
    if (splitByGrade) {
      return enrichedCgm;
    }

    // Step 4: merge rows that share the same itemId (same item spread across multiple warehouses)
    // Quantities and amounts are summed from the already-enriched per-cgm values to stay accurate
    const itemMap = {};
    enrichedCgm.forEach((r) => {
      const itemKey = r.itemId != null ? r.itemId : `cgm-${r.customerGradeMark}`;
      if (!itemMap[itemKey]) {
        itemMap[itemKey] = { ...r, cgmCodes: [r.customerGradeMark] };
      } else {
        itemMap[itemKey].totalIncoming  += r.totalIncoming;
        itemMap[itemKey].totalOutgoing  += r.totalOutgoing;
        itemMap[itemKey].netQuantity    += r.netQuantity;
        itemMap[itemKey].totalAmountUsd += r.totalAmountUsd;
        itemMap[itemKey].cgmCodes.push(r.customerGradeMark);
      }
    });

    return Object.values(itemMap).map((r) => ({
      ...r,
      customerGradeMark: r.cgmCodes.join(", "),
    }));
  }, [rows, splitByGrade]);

  const sorted = useMemo(() => {
    const cmp =
      order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
    return [...aggregated].sort(cmp);
  }, [aggregated, order, orderBy]);

  const totals = useMemo(
    () =>
      aggregated.reduce(
        (acc, r) => ({
          totalIncoming:  acc.totalIncoming  + (Number(r.totalIncoming)  || 0),
          totalOutgoing:  acc.totalOutgoing  + (Number(r.totalOutgoing)  || 0),
          netQuantity:    acc.netQuantity    + (Number(r.netQuantity)    || 0),
          totalAmountUsd: acc.totalAmountUsd + (Number(r.totalAmountUsd) || 0),
        }),
        { totalIncoming: 0, totalOutgoing: 0, netQuantity: 0, totalAmountUsd: 0 },
      ),
    [aggregated],
  );

  const exportData = sorted.map((r) => ({
    "Item ID":              r.itemId ?? "",
    Code:                   r.customerGradeMark ?? "",
    Name:                   r.name ?? "",
    NIC:                    r.itemNicotine ?? "",
    SUGAR:                  r.itemSugar ?? "",
    "Unit Cost":            r.unitCost ?? "",
    Currency:               r.currency ?? "",
    "Unit Cost In USD":     r.unitCostInUsd ?? "",
    "Incoming Qty":         r.totalIncoming,
    "Outgoing Qty":         r.totalOutgoing,
    "Sum of Quantity":      r.netQuantity,
    "Sum of Amount In USD": r.totalAmountUsd,
  }));

  const handleSort = (colId) => {
    setOrder(orderBy === colId && order === "asc" ? "desc" : "asc");
    setOrderBy(colId);
  };

  const headerSx = {
    backgroundColor: "#20315d",
    color: "white",
    whiteSpace: "nowrap",
    fontWeight: 700,
    py: 1,
  };

  return (
    <>
      <Button variant="outlined" size="small" onClick={() => setOpen(true)}>
        Customer Grade Mark Based Inventory
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#20315d",
            color: "white",
            py: 1.5,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            Customer Grade Mark Based Inventory
          </Typography>
          <Tooltip title="Close">
            <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <TableContainer component={Paper} sx={{ maxHeight: 520, mt: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align}
                      sx={headerSx}
                      sortDirection={orderBy === col.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() => handleSort(col.id)}
                        sx={{
                          color: "white !important",
                          "& .MuiTableSortLabel-icon": { color: "rgba(255,255,255,0.7) !important" },
                          "&.Mui-active .MuiTableSortLabel-icon": { color: "white !important" },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No records with a Customer Grade Mark found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sorted.map((r, idx) => (
                    <TableRow
                      key={splitByGrade ? r.customerGradeMark : (r.itemId ?? r.customerGradeMark)}
                      sx={{
                        backgroundColor: idx % 2 === 0 ? "white" : "#f5f7fa",
                        "&:hover": { backgroundColor: "#e8edf5" },
                      }}
                    >
                      <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {r.customerGradeMark}
                      </TableCell>
                      <TableCell align="center">{r.itemId ?? "-"}</TableCell>
                      <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>{r.name || "-"}</TableCell>
                      <TableCell align="center">{r.itemNicotine ?? "-"}</TableCell>
                      <TableCell align="center">{r.itemSugar ?? "-"}</TableCell>
                      <TableCell align="right">{fmt(r.unitCost, 4)}</TableCell>
                      <TableCell align="center">{r.currency ?? "-"}</TableCell>
                      <TableCell align="right">{fmt(r.unitCostInUsd, 4)}</TableCell>
                      <TableCell align="right">{fmt(r.totalIncoming)}</TableCell>
                      <TableCell align="right">{fmt(r.totalOutgoing)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#1E2685" }}>
                        {fmt(r.netQuantity)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#1E2685" }}>
                        {fmt(r.totalAmountUsd)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>

              {sorted.length > 0 && (
                <TableFooter>
                  <TableRow sx={{ backgroundColor: "#20315d", position: "sticky", bottom: 0, zIndex: 2 }}>
                    <TableCell colSpan={8} sx={{ color: "white", fontWeight: 700, py: 1 }}>
                      TOTAL ({sorted.length} items)
                    </TableCell>
                    <TableCell align="right" sx={{ color: "white", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {fmt(totals.totalIncoming)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "white", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {fmt(totals.totalOutgoing)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "white", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {fmt(totals.netQuantity)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "white", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {fmt(totals.totalAmountUsd)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {sorted.length} record(s)
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={splitByGrade}
                  onChange={(e) => setSplitByGrade(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Split by Grade
                </Typography>
              }
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <ExportExcel
              excelData={exportData}
              fileName={splitByGrade ? "CustomerGradeMarkInventory_Split" : "CustomerGradeMarkInventory"}
              buttonLabel="Export to Excel"
            />
            <Button onClick={() => setOpen(false)} variant="outlined" size="small">
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerGradeMarkInventoryButton;

